import type { AppConfig, DepConfigResolved, FormYaml, ModuleYaml } from "@runtime/runner/data";
import { CodeItemResponse } from "./shared-types";
import * as windowRpc from "./windowRpc";

// Simple cache - TODO we'll have to invalidate this

export interface ItemIndexEntry {
    fullName: string;
    isPackage?: boolean;
}

interface DepIndex {
    forms: ItemIndexEntry[];
    modules: ItemIndexEntry[];
    packageName?: string;
    config: DepConfigResolved;
    clientInitModule: string;
}

export interface AppIndex {
    dependencyOrder: string[];
    dependencies: { [depId: string]: DepIndex };
    forms: ItemIndexEntry[];
    modules: ItemIndexEntry[];
    serverModules?: ItemIndexEntry[];
    config: DepConfigResolved;
    clientInitModule?: string;
}

export interface TemplateSpec {
    depId: string | null;
    relevantForms: string[];
    appPackage: string;
}

export interface ItemType {
    type: "form" | "module";
    fullName: string;
    depId: string | null;
}

export let appIndex: AppIndex;

export let appConfig: AppConfig;

export let mainPackageName: string;

export const setAppIndex = (index: AppIndex) => {
    appIndex = index;
};

export const setAppConfig = (config: AppConfig) => {
    appConfig = config;
    mainPackageName = appConfig.package_name || "main_app_package";
};

export const setApp = (config: AppConfig, index: AppIndex) => {
    setAppConfig(config);
    setAppIndex(index);
};

const findDepIndex = (appPackage: string) =>
    appPackage === mainPackageName
        ? ([null, appIndex] as const)
        : Object.entries(appIndex.dependencies).find(([_, { packageName }]) => packageName === appPackage)!;

const findItemType = (appPackage: string, fullName: string, isPackage: boolean) => {
    const match = (e: ItemIndexEntry) => e.fullName === fullName && !!e.isPackage === isPackage;
    const findType = (index: DepIndex | AppIndex, depId: string | null): ItemType | null =>
        index.forms.find(match)
            ? { type: "form", fullName, depId }
            : index.modules.find(match)
            ? { type: "module", fullName, depId }
            : null;

    const foundDep = findDepIndex(appPackage);
    return foundDep && findType(foundDep[1], foundDep[0]);
};

export const findDepId = (packageName: string) => {
    const foundDep = findDepIndex(packageName);
    return foundDep?.[0] || undefined;
};

export const findSourceCode = (path: string) => {
    const match = path.match(/^app\/([^/]+)\/(.*?([^/.]+))(\/__init__\.py|\.py)$/);
    if (!match) {
        return;
    }
    const [_, appPackage, fullPath, leafName, pkgSuffix] = match;
    const fullName = fullPath.replaceAll("/", ".");
    const isPackage = pkgSuffix !== ".py";
    return findItemType(appPackage, fullName, isPackage);
};

const isFormOrModuleCodeItem = (item: CodeItemResponse): item is ModuleYaml | FormYaml => !!item && "code" in item;

export const getSourceCode = async ({ type, fullName, depId }: ItemType) => {
    const item = await getCodeItem(type, fullName, depId);
    if (!isFormOrModuleCodeItem(item)) {
        throw new Error(`No source code for ${type} ${fullName}`);
    }
    return item.code;
};

export const findDesignerTemplates = (path: string) => {
    const match = path.match(/^app\/([^/]+)(?:\/(.+))?\/_anvil_designer.js$/);
    if (!match) {
        return;
    }
    const [_, appPackage, packagePath] = match;
    const packageName = packagePath.replaceAll("/", ".");

    const foundDep = findDepIndex(appPackage);
    if (!foundDep) {
        return;
    }
    const [depId, index] = foundDep;

    const relevantForms = index.forms
        .filter(({ fullName, isPackage }) =>
            isPackage ? fullName === packageName : fullName.replace(/\.[^.]+$/, "") === packageName
        )
        .map(({ fullName }) => fullName);

    return { depId, relevantForms, appPackage };
};

const inflightCodeRequests = new Map<string, Promise<Promise<CodeItemResponse>>>();
export const cachedCodeItems = new Map<string, CodeItemResponse>();

export const removeCachedCodeItemResponse = (type: string, name: string, depId: string | null) => {
    cachedCodeItems.delete(`${type}:${name}:${depId}`);
};

// circular imports mean we can't access editState.editingFrom.className
let editingFormName = "";

export const ensureEditingFormIsNotCached = (name: string) => {
    editingFormName = name;
    removeCachedCodeItemResponse("form", name, null);
};

export function cacheCodeItemResponse(type: string, name: string, depId: string | null, item: CodeItemResponse) {
    const key = `${type}:${name}:${depId}`;
    if (name !== editingFormName) {
        // editForm might not be set yet - when we set it we also remove from the cache
        cachedCodeItems.set(key, item);
    }
}

export const clearCachedCodeItems = () => cachedCodeItems.clear();

export const getCodeItem = async (type: "form" | "module" | "asset", name: string, depId: string | null) => {
    const key = `${type}:${name}:${depId}`;
    const cachedYaml = cachedCodeItems.get(key);
    if (cachedYaml) {
        return cachedYaml;
    }
    const inflight = inflightCodeRequests.get(key);
    let item;
    if (inflight) {
        item = await inflight;
    } else {
        const inflight = windowRpc.call("getCodeItem", type, name, depId);
        inflightCodeRequests.set(key, inflight);
        item = await inflight;
        inflightCodeRequests.delete(key);
    }
    cacheCodeItemResponse(type, name, depId, item);
    return item;
};

export const getFormYaml = async (depId: string | null, formName: string) => {
    return (await getCodeItem("form", formName, depId)) as FormYaml;
};

export const getMultiFormYaml = async ({ depId, relevantForms }: TemplateSpec) => {
    const forms = [];
    for (let name of relevantForms) {
        forms.push(await getFormYaml(depId, name));
    }
    return forms;
};

export function getAppPackage(depId: string | null) {
    return depId ? appIndex.dependencies[depId]?.packageName : mainPackageName;
}

export function getDepId(appPackage: string) {
    return appPackage === mainPackageName
        ? null
        : Object.keys(appIndex.dependencies).find((depId) => appIndex.dependencies[depId].packageName === appPackage);
}

const getItemEntry = (items: ItemIndexEntry[] | undefined, name: string) =>
    items && items.find(({ fullName }) => fullName === name);

export function getFormIndexEntry(depId: string | null, formName: string) {
    return getItemEntry(depId ? appIndex.dependencies[depId]?.forms || [] : appIndex.forms, formName);
}

export function isForm(depId: string | null, formName: string) {
    return !!getFormIndexEntry(depId, formName);
}

export function isModule(depId: string | null, formName: string) {
    return !!getItemEntry(depId ? appIndex.dependencies[depId]?.modules || [] : appIndex.modules, formName);
}
